import React, { useEffect, useState } from 'react'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import AddIcon from '@mui/icons-material/Add'
import CheckIcon from '@mui/icons-material/Check'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { InviteStudentLicense, LicenseType } from '../../services/api/api'
import style from './style.less'
import { useIntl } from 'react-intl'
import { formatTrainingPathLabel, getTrainingPathsForLicense } from '../../helpers/trainingPathData'

type LicenseRow = Partial<InviteStudentLicense> & { confirmed?: boolean }

const isReadyToConfirm = (row: LicenseRow): row is InviteStudentLicense & { confirmed?: boolean } =>
  !!row.licenseTypeId && !!row.learningPathId

const toInitialRows = (licenses: InviteStudentLicense[] | undefined, licenseTypes: LicenseType[]): LicenseRow[] => {
  if (!licenses?.length) return [{}]
  const confirmed = licenses.map((license) => ({ ...license, confirmed: true }))
  const usedIds = new Set(confirmed.map((row) => row.licenseTypeId))
  const canAddMore = licenseTypes.some((lt) => !usedIds.has(lt.id))
  return canAddMore ? [...confirmed, {}] : confirmed
}

const getUsedLicenseTypeIds = (rows: LicenseRow[], excludeIndex: number): Set<string> =>
  new Set(
    rows
      .filter((_, i) => i !== excludeIndex && rows[i].licenseTypeId)
      .map((row) => row.licenseTypeId as string)
  )

const isDuplicateLicenseType = (rows: LicenseRow[], index: number): boolean => {
  const licenseTypeId = rows[index]?.licenseTypeId
  if (!licenseTypeId) return false
  return rows.some((row, i) => i !== index && row.licenseTypeId === licenseTypeId)
}

interface LicensesWithDetailsInputProps {
  licenseTypes: LicenseType[]
  fieldName: string
  onChange: (name: string, value: InviteStudentLicense[] | undefined) => void
  defaultValue?: InviteStudentLicense[]
}

const LicensesWithDetailsInput: React.FunctionComponent<LicensesWithDetailsInputProps> = ({
  licenseTypes,
  fieldName,
  onChange,
  defaultValue,
}) => {
  const intl = useIntl()
  const [rows, setRows] = useState<LicenseRow[]>(() => toInitialRows(defaultValue, licenseTypes))

  useEffect(() => {
    const confirmed = rows.filter((r) => r.confirmed && isReadyToConfirm(r))
      .map(({ confirmed: _, licenseTypeId, learningPathId }) => ({ licenseTypeId: licenseTypeId!, learningPathId: learningPathId! }))
    onChange(fieldName, confirmed.length > 0 ? confirmed : undefined)
  }, [rows])

  const updateRow = (index: number, field: keyof InviteStudentLicense, value: string) => {
    setRows((prev) => {
      const next = [...prev]
      const updated = { ...next[index], [field]: value }
      if (field === 'licenseTypeId') {
        updated.learningPathId = ''
        const licenseCode = licenseTypes.find((lt) => lt.id === value)?.code ?? ''
        if (licenseCode) {
          const paths = getTrainingPathsForLicense(licenseCode)
          if (paths.length === 1) updated.learningPathId = paths[0].id
        }
      }
      next[index] = updated
      return next
    })
  }

  const confirmRow = (index: number) => {
    setRows((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], confirmed: true }
      return next
    })
  }

  const addRow = () => setRows((prev) => [...prev, {}])

  const removeRow = (index: number) =>
    setRows((prev) => prev.filter((_, i) => i !== index))

  const getConfirmTooltip = (row: LicenseRow, index: number): string => {
    if (isDuplicateLicenseType(rows, index)) {
      return intl.formatMessage(
        { id: 'license_tooltip_duplicate_license' },
        { defaultMessage: 'This license class has already been added' }
      )
    }
    if (!row.licenseTypeId) return intl.formatMessage({ id: 'license_tooltip_select_license' })
    if (!row.learningPathId) return intl.formatMessage({ id: 'license_tooltip_select_training_path' })
    return ''
  }

  const trainingPathLabel = (trainingPathId: string) =>
    formatTrainingPathLabel(intl, trainingPathId)

  const confirmedRows = rows.filter((r) => r.confirmed && isReadyToConfirm(r))
  const pendingRows = rows.filter((r) => !r.confirmed)
  const usedLicenseTypeIds = new Set(rows.filter((r) => r.licenseTypeId).map((r) => r.licenseTypeId as string))
  const canAddMoreLicenses = licenseTypes.some((lt) => !usedLicenseTypeIds.has(lt.id))

  return (
    <div className={style.section}>
      <div className={style.sectionHeader}>
        <div className={style.sectionIcon}>
          <BadgeOutlinedIcon fontSize="small" />
        </div>
        <div className={style.sectionHeaderText}>
          <h4 className={style.sectionTitle}>
            {intl.formatMessage({ id: 'license_section_title' }, { defaultMessage: 'Driving licenses' })} *
          </h4>
          <p className={style.sectionDescription}>
            {intl.formatMessage(
              { id: 'license_section_description' },
              { defaultMessage: 'At least one driving license is required before the student can be created.' }
            )}
          </p>
        </div>
      </div>

      {pendingRows.length > 0 && (
        <div className={style.pendingList}>
          {pendingRows.map((row) => {
            const globalIndex = rows.indexOf(row)
            const selectedLicenseCode = licenseTypes.find((lt) => lt.id === row.licenseTypeId)?.code ?? ''
            const usedInOtherRows = getUsedLicenseTypeIds(rows, globalIndex)
            const selectableLicenseTypes = licenseTypes.filter(
              (lt) => lt.id === row.licenseTypeId || !usedInOtherRows.has(lt.id)
            )
            const availablePaths = selectedLicenseCode
              ? getTrainingPathsForLicense(selectedLicenseCode)
              : []
            return (
              <div key={globalIndex} className={style.pendingRow}>
                <div className={style.fieldGroup}>
                  <span className={style.fieldLabel}>
                    {intl.formatMessage({ id: 'license_type' }, { defaultMessage: 'License type' })}
                  </span>
                  <FormControl className={`${style.select} ${style.selectControl}`} size="small">
                    <Select
                      displayEmpty
                      value={row.licenseTypeId ?? ''}
                      onChange={(e) => updateRow(globalIndex, 'licenseTypeId', e.target.value)}
                      renderValue={(v) =>
                        v ? licenseTypes.find((lt) => lt.id === v)?.code ?? v
                          : intl.formatMessage(
                            { id: 'license_select_class' },
                            { defaultMessage: 'Select license type' }
                          )
                      }
                    >
                      {selectableLicenseTypes.map((lt) => (
                        <MenuItem key={lt.id} value={lt.id}>
                          {lt.code}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>

                <div className={`${style.fieldGroup} ${style.fieldGroupWide}`}>
                  <span className={style.fieldLabel}>
                    {intl.formatMessage({ id: 'license_training_path_placeholder' }, { defaultMessage: 'Training path' })}
                  </span>
                  <FormControl className={`${style.selectWide} ${style.selectControl}`} size="small">
                    <Select
                      displayEmpty
                      disabled={!selectedLicenseCode}
                      value={row.learningPathId ?? ''}
                      onChange={(e) => updateRow(globalIndex, 'learningPathId', e.target.value)}
                      renderValue={(v) =>
                        v
                          ? trainingPathLabel(String(v))
                          : intl.formatMessage(
                            { id: 'license_select_training_path' },
                            { defaultMessage: 'Select training path' }
                          )
                      }
                    >
                      {availablePaths.map((entry) => (
                        <MenuItem key={entry.id} value={entry.id}>
                          {trainingPathLabel(entry.id)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>

                <div className={style.actionButtons}>
                  <Tooltip title={!isReadyToConfirm(row) || isDuplicateLicenseType(rows, globalIndex) ? getConfirmTooltip(row, globalIndex) : ''}>
                    <span className={style.tooltipWrapper}>
                      <IconButton
                        size="small"
                        onClick={() => confirmRow(globalIndex)}
                        disabled={!isReadyToConfirm(row) || isDuplicateLicenseType(rows, globalIndex)}
                        className={style.confirmButton}
                      >
                        <CheckIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>

                  <Tooltip title={rows.length === 1 ? intl.formatMessage({ id: 'license_tooltip_cannot_remove' }) : ''}>
                    <span className={style.tooltipWrapper}>
                      <IconButton
                        size="small"
                        onClick={() => removeRow(globalIndex)}
                        disabled={rows.length === 1}
                        className={style.removeButton}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {canAddMoreLicenses && (
        <button type="button" onClick={addRow} className={style.addButton}>
          <AddIcon fontSize="small" />
          {intl.formatMessage({ id: 'license_add_another' }, { defaultMessage: 'Add another driving license' })}
        </button>
      )}

      {confirmedRows.length > 0 ? (
        <div className={style.completedList}>
          {confirmedRows.map((row) => {
            const globalIndex = rows.indexOf(row)
            const licenseCode = licenseTypes.find((lt) => lt.id === row.licenseTypeId)?.code ?? row.licenseTypeId
            return (
              <div key={globalIndex} className={style.completedRow}>
                <span className={style.completedBadge}>{licenseCode}</span>
                <span className={style.completedSeparator}>·</span>
                <span className={style.completedText}>{trainingPathLabel(row.learningPathId!)}</span>
                <IconButton
                  size="small"
                  onClick={() => removeRow(globalIndex)}
                  className={style.completedRemoveButton}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </div>
            )
          })}
        </div>
      ) : (
        <div className={style.emptyState}>
          <InfoOutlinedIcon className={style.emptyStateIcon} fontSize="small" />
          <span>
            {intl.formatMessage(
              { id: 'license_empty_state' },
              { defaultMessage: 'No driving license added yet.' }
            )}
          </span>
        </div>
      )}
    </div>
  )
}

export default LicensesWithDetailsInput
